import { useState, VFC } from "react";
// API
import { UploadCsv_uploadCsv } from "api/Import/CustomImport/types/UploadCsv";
import { GetActivityTemplates_activityTemplates } from "api/MyLivestock/Activity/types/GetActivityTemplates";
// Utils
import { ACTIVITIES_IMPORT, ANIMALS_IMPORT } from "constants/Import";
import {
  useEffectSpeciesActive,
  useEventRequest,
  useGetAnimalTypes,
  useMutationImportCsv,
  useStateSpecies,
} from "hooks";
import { useColumnFieldMap } from "./hooks";
// Components
import { Button, Flex, InputCheckbox, Paper, Spacer, Text } from "components";
import { ImportWizardActivityTemplateSelect } from "./ImportWizardActivityTemplateSelect";
import { ImportWizardColumnsTable } from "./ImportWizardColumnsTable";
// Resources
import styles from "./ImportWizardChooseColumns.module.scss";

interface Hacky_UploadCsv_uploadCsv_fields {
  field: string;
  isRequired: boolean;
  label: string;
}

export interface Hacky_UploadCsv_uploadCsv extends Omit<UploadCsv_uploadCsv, "fields"> {
  fields: Hacky_UploadCsv_uploadCsv_fields[];
}

interface ImportWizardChooseColumnsProps {
  activityTemplate?: GetActivityTemplates_activityTemplates;
  file: Hacky_UploadCsv_uploadCsv;
  importType: typeof ANIMALS_IMPORT | typeof ACTIVITIES_IMPORT;
  onChangeActivityTemplate: (activityTemplate: GetActivityTemplates_activityTemplates) => void;
  onClickBack: () => void;
  onRestart: () => void;
  onSuccess: (taskId: string) => void;
}

export const ImportWizardChooseColumns: VFC<ImportWizardChooseColumnsProps> = ({
  activityTemplate,
  file,
  importType,
  onChangeActivityTemplate,
  onClickBack,
  onRestart,
  onSuccess,
}) => {
  // isFirstRowColumnNames
  const [isFirstRowColumnNames, setIsFirstRowColumnNames] = useState<boolean>(true);
  const handleToggleIsFirstRowColumnNames = () => setIsFirstRowColumnNames(!isFirstRowColumnNames);
  const fileRowsCount = file.totalRows || 0; // sadly the schema generates `number | null` so falling back to 0 here
  const rowsToImportCount = isFirstRowColumnNames ? fileRowsCount - 1 : fileRowsCount;

  // Column field mapping
  const {
    columnFieldMap,
    fieldOptions,
    isMatchColumnsDisabled,
    isRequiredFieldMissing,
    matchColumns: handleClickMatchColumns,
    setColumn: handleChangeColumnField,
  } = useColumnFieldMap({
    activityTemplate,
    file,
    isFirstRowColumnNames,
  });

  // Import query
  const { active: speciesActive } = useStateSpecies();
  const { data: animalTypes } = useGetAnimalTypes();
  const speciesCattle = animalTypes?.animalTypes?.find((item) => item?.slug === "cattle");
  const speciesActiveId = speciesActive ? speciesActive.id : speciesCattle?.id;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [importCsvMutation] = useMutationImportCsv();

  const { eventsRequest } = useEventRequest();
  const handleImportFile = async () => {
    if (!file.id) throw new Error("Has no ID");

    setIsLoading(true);

    try {
      const animalProperties =
        importType === ANIMALS_IMPORT
          ? {
              animalType: speciesActiveId ? Number(speciesActiveId) : undefined,
            }
          : {};

      const activityProperties =
        importType === ACTIVITIES_IMPORT && activityTemplate
          ? {
              activityTemplate: Number(activityTemplate.id),
            }
          : {};

      const { data } = await importCsvMutation({
        variables: {
          input: {
            id: +file.id,
            fields: columnFieldMap,
            isFirstRowColumnNames: isFirstRowColumnNames,
            ...animalProperties,
            ...activityProperties,
          },
        },
      });

      if (data?.importCsv?.errors) {
        setError(data.importCsv.errors.map((error) => error?.message).join(". "));
      } else if (data?.importCsv?.taskId) {
        if (importType === ACTIVITIES_IMPORT) {
          eventsRequest({ name: "import-advanced-activities" }).catch(() => null);
        } else {
          eventsRequest({ name: "import-advanced-animals" }).catch(() => null);
        }

        onSuccess(data.importCsv.taskId);
      }
    } catch (error: any) {
      if (error.networkError) {
        let newError = error.networkError.message;

        if (error.networkError.result.errors.length) {
          const resultErrors = error.networkError.result.errors.map((error) => error.message).join(". ");
          newError += `. ${resultErrors}`;
        }

        setError(newError);
      } else if (error.graphQLErrors.length) {
        setError(error.graphQLErrors[0].message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Navigation safety
  const handleEffectSpeciesActive = () => {
    if (importType !== ANIMALS_IMPORT) return;

    onRestart();
  };
  useEffectSpeciesActive(handleEffectSpeciesActive);

  return (
    <>
      <Paper className={styles.import_wizard_choose_columns} dark>
        <Spacer baselineHeight={1} />

        <Flex container containerAlignItems="center" containerJustifyContent="space-between">
          <Flex item itemGutter>
            <Text smaller>Choose columns to assign to fields to complete your import</Text>
          </Flex>

          <Flex item itemGutter>
            <InputCheckbox
              checked={isFirstRowColumnNames}
              label="First row is column names"
              name="isFirstRowColumnNames"
              onChange={handleToggleIsFirstRowColumnNames}
            />
          </Flex>
        </Flex>

        <Spacer baselineHeight={1} />

        <Flex container containerJustifyContent={activityTemplate ? "space-between" : "flex-end"}>
          {activityTemplate && (
            <Flex item itemGutter xs={2}>
              <ImportWizardActivityTemplateSelect
                activityTemplate={activityTemplate}
                onChangeActivityTemplate={onChangeActivityTemplate}
              />
            </Flex>
          )}

          <Flex item itemGutter>
            <Button
              caption="Match columns"
              colour="grey"
              disabled={!isFirstRowColumnNames || isMatchColumnsDisabled}
              onClick={handleClickMatchColumns}
              variant="hollow"
            />
          </Flex>
        </Flex>

        <Spacer baselineHeight={1} />
      </Paper>

      <Spacer baselineHeight={2} />

      <ImportWizardColumnsTable
        columnFieldMap={columnFieldMap}
        fieldOptions={fieldOptions}
        file={file}
        isFirstRowColumnNames={isFirstRowColumnNames}
        onChangeColumnField={handleChangeColumnField}
      />

      <Spacer baselineHeight={2} />

      <Flex container containerJustifyContent="space-between">
        <Text smaller>Total rows to be imported: {rowsToImportCount}</Text>

        {error && (
          <Text smaller warning>
            {error}
          </Text>
        )}
      </Flex>

      <Spacer baselineHeight={4} />

      <Flex container containerJustifyContent="space-between">
        <Button caption="Back" colour="grey" onClick={onClickBack} variant="hollow" />

        <Button
          caption="Finish upload"
          disabled={isLoading || isRequiredFieldMissing}
          onClick={handleImportFile}
          requesting={isLoading}
        />
      </Flex>
    </>
  );
};
